import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from 'convex/react';
import { api } from '../../../convex/_generated/api'; // DEEP_IMPORT_JUSTIFIED: pages in src/pages/insurance/ require three levels up
import type { Id } from '../../../convex/_generated/dataModel'; // DEEP_IMPORT_JUSTIFIED: standard convex _generated import pattern
import { useOrgContext } from '@/hooks/useOrgContext';
import { useProviderForm } from '@/hooks/useProviderForm';
import { ProviderContactCard } from '@/components/insurance/ProviderContactCard';
import { PortalSettingsSection } from '@/components/insurance/PortalSettingsSection';
import { CountryCombobox } from '@/components/insurance/CountryCombobox';
import { getPredefinedProviders } from '@/lib/predefinedProviders';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ArrowLeft, Building2, UserPlus } from 'lucide-react';
import type { IPredefinedProvider } from '@/@types/insurance';

export function ProviderFormPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { currentOrg, isLoading: orgLoading } = useOrgContext();

  const isEditing = id !== undefined && id !== 'new';
  const providerId = isEditing ? (id as Id<'insuranceProviders'>) : null; // AS_JUSTIFIED: one-time Id cast from route params

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const form = useProviderForm();

  const predefinedProviders = getPredefinedProviders(form.country);
  const hasPredefined = predefinedProviders.length > 0;

  const handlePredefinedSelect = (providerName: string) => {
    const provider = predefinedProviders.find((p: IPredefinedProvider) => p.name === providerName);
    if (provider) {
      form.setName(provider.name);
      form.setWebsite(provider.website);
    }
  };

  const existingProvider = useQuery(
    api['insurance/providers'].get,
    providerId ? { providerId } : 'skip'
  );

  const createProvider = useMutation(api['insurance/providers'].create);
  const updateProvider = useMutation(api['insurance/providers'].update);

  useEffect(() => {
    if (!isEditing || existingProvider === undefined || existingProvider === null) return;
    form.initFromExisting(existingProvider);
  }, [isEditing, existingProvider]); // eslint-disable-line react-hooks/exhaustive-deps

  const isLoading = orgLoading || (isEditing && existingProvider === undefined);

  const handleSubmit = async () => {
    if (!form.name.trim()) {
      setSubmitError('Provider name is required');
      return;
    }
    if (!currentOrg) {
      setSubmitError('No organisation found');
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      if (isEditing && providerId) {
        await updateProvider({
          providerId,
          name: form.name,
          country: form.country || undefined,
          website: form.website || undefined,
          contacts: form.contacts,
        });
      } else {
        await createProvider({
          orgId: currentOrg._id,
          name: form.name,
          country: form.country || undefined,
          website: form.website || undefined,
          contacts: form.contacts,
        });
      }
      navigate('/insurance/providers');
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Failed to save provider');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!orgLoading && !currentOrg) {
    return (
      <div className="min-h-full bg-background-secondary py-8 px-10">
        <div className="flex flex-col items-center justify-center py-24 text-center">
          <Building2 className="w-12 h-12 text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-1">No organisation found</h3>
          <p className="text-sm text-muted-foreground">
            You need to be part of an organisation to manage insurance providers.
          </p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-full bg-background-secondary py-8 px-10">
        <div className="flex items-center justify-center py-16">
          <p className="text-sm text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-background-secondary py-8 px-10">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate('/insurance/providers')}
          className="shrink-0"
        >
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div>
          <h1 className="text-2xl font-semibold text-foreground">
            {isEditing ? 'Edit Provider' : 'New Provider'}
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            {isEditing
              ? `Editing ${existingProvider?.name ?? ''}`
              : 'Create a new insurance provider'}
          </p>
        </div>
      </div>

      {/* Form card */}
      <div className="max-w-2xl">
        <div className="bg-card border border-border rounded-xl p-6 flex flex-col gap-5">
          {/* Country */}
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-foreground">Country</label>
            <CountryCombobox
              value={form.country}
              onChange={form.setCountry}
              placeholder="Select country"
              disabled={isSubmitting}
            />
          </div>

          {/* Predefined provider selector — only visible when country has predefined providers */}
          {hasPredefined && (
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-foreground">Predefined Provider</label>
              <Select onValueChange={handlePredefinedSelect}>
                <SelectTrigger className="w-full" disabled={isSubmitting}>
                  <SelectValue placeholder="Select a predefined provider to auto-fill..." />
                </SelectTrigger>
                <SelectContent>
                  {predefinedProviders.map((p: IPredefinedProvider) => (
                    <SelectItem key={p.name} value={p.name}>
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Selecting a predefined provider will fill in the name and website. You can still
                edit them.
              </p>
            </div>
          )}

          {/* Provider name */}
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-foreground">
              Provider Name <span className="text-destructive">*</span>
            </label>
            <Input
              value={form.name}
              onChange={e => form.setName(e.target.value)}
              placeholder="e.g. Allianz"
              disabled={isSubmitting}
            />
          </div>

          {/* Website */}
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-foreground">Website</label>
            <Input
              type="url"
              value={form.website}
              onChange={e => form.setWebsite(e.target.value)}
              placeholder="https://www.example.com"
              disabled={isSubmitting}
            />
          </div>

          {/* Contacts section */}
          <div className="flex flex-col gap-4 pt-2 border-t border-border">
            <div className="flex items-center justify-between">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Contacts ({form.contacts.length}/5)
              </p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={form.addContact}
                disabled={isSubmitting || form.contacts.length >= 5}
              >
                <UserPlus className="w-4 h-4 mr-1.5" />
                Add Contact
              </Button>
            </div>

            {form.contacts.length === 0 && (
              <p className="text-sm text-muted-foreground py-2">
                No contacts added yet. Click &quot;Add Contact&quot; to add one.
              </p>
            )}

            {form.contacts.map((contact, index) => (
              <ProviderContactCard
                key={index}
                index={index}
                contact={contact}
                onUpdate={form.updateContact}
                onRemove={form.removeContact}
                disabled={isSubmitting}
              />
            ))}
          </div>

          {/* Portal Settings — only shown when editing an existing provider */}
          {isEditing && providerId && currentOrg && (
            <PortalSettingsSection
              providerId={providerId}
              orgId={currentOrg._id}
              providerName={form.name}
            />
          )}

          {/* Error */}
          {submitError && <p className="text-sm text-destructive">{submitError}</p>}

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-2 border-t border-border">
            <Button
              variant="outline"
              onClick={() => navigate('/insurance/providers')}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : isEditing ? 'Save Changes' : 'Create Provider'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
